import { useNavigate } from "react-router-dom";
import useUserStore from "../store/userStore";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import useRegister from "../hooks/useRegister";
import useListCountries from "../hooks/useListCountries";
import Country from "../interfaces/country";

interface UserRegister {
  username: string;
  email: string;
  password: string;
  country: number;
}

interface SubmitUser {
  username: string;
  email: string;
  password: string;
  country: Country;
}

const RegisterForm = () => {
  const { data, isPending, isError } = useListCountries();

  const schema = z.object({
    username: z.string().min(1, { message: "Username is required" }),
    password: z.string().min(1, { message: "Password is required" }),
    confirmPassword: z.string().min(1, { message: "Please confirm your password" }),
    email: z
      .string()
      .min(1, { message: "Email is required" })
      .email({ message: "Invalid email address" }),
    country: z.number().refine((val) => val > 0, { message: "Country is required" }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

  const token = useUserStore((state) => state.token);
  const destinyRoute = useUserStore((state) => state.destinyRoute);

  const setToken = useUserStore((state) => state.setToken);


  const navigate = useNavigate();

  const { mutate: registerSubmit, error: registerError } = useRegister();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema) });

  const submitRegister = ({ username, email, password, country }: UserRegister) => {
    const submitCountry = {id: country} as Country;
    const newUser: SubmitUser = { username, email, password, country: submitCountry };
    registerSubmit(newUser, {
      onSuccess: (data) => {
        if (data.token) {
          setToken(data);
          navigate(destinyRoute || "/");
        } else {
          setToken({ token: "", username: "", isAdmin: false });
        }
      },
    });
  };
  useEffect(() => {
    if (token) {
      navigate("/");
    }
  }, [token, navigate]);

  return (
    <div
      className="d-flex align-items-center justify-content-center"
      style={{
        minHeight: "calc(100vh - 56px)",
        backgroundColor: "#1d1f1c",
        color: "#c2c2c2",
        paddingTop: "56px",
        paddingBottom: "5px"
      }}
    >
      <div className="col-md-6 col-lg-4">
        <div
          className="card p-4 shadow-lg"
          style={{ backgroundColor: "#2a2d2a", border: "1px solid #3c403c" }}
        >
          <h2 className="text-center mb-4" style={{ color: "#e0e0e0" }}>
            Register
          </h2>
          {registerError && (
            <div className="alert alert-danger" role="alert">
              Username or Email already in use.
            </div>
          )}
          <form onSubmit={handleSubmit(submitRegister)}>
            <div className="mb-3">
              <label htmlFor="username" className="form-label">
                Username
              </label>
              <input
                type="text"
                id="username"
                {...register("username")}
                className={`form-control ${
                  errors.username ? "is-invalid" : ""
                }`}
                style={{
                  backgroundColor: "#333633",
                  color: "#c2c2c2",
                  borderColor: "#4a4d4a",
                }}
              />
              <div className="invalid-feedback" style={{ color: "#ff8888" }}>
                {errors.username?.message}
              </div>
            </div>
            <div className="mb-3">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                type="email"
                id="email"
                {...register("email")}
                className={`form-control ${errors.email ? "is-invalid" : ""}`}
                style={{
                  backgroundColor: "#333633",
                  color: "#c2c2c2",
                  borderColor: "#4a4d4a",
                }}
              />
              <div className="invalid-feedback" style={{ color: "#ff8888" }}>
                {errors.email?.message}
              </div>
            </div>
            <div className="mb-4">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <input
                type="password"
                id="password"
                {...register("password")}
                className={`form-control ${
                  errors.password ? "is-invalid" : ""
                }`}
                style={{
                  backgroundColor: "#333633",
                  color: "#c2c2c2",
                  borderColor: "#4a4d4a",
                }}
              />
              <div className="invalid-feedback" style={{ color: "#ff8888" }}>
                {errors.password?.message}
              </div>
            </div>
            <div className="mb-4">
              <label htmlFor="confirmPassword" className="form-label">
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                {...register("confirmPassword")}
                className={`form-control ${errors.confirmPassword ? "is-invalid" : ""}`}
                style={{
                  backgroundColor: "#333633",
                  color: "#c2c2c2",
                  borderColor: "#4a4d4a",
                }}
              />
              <div className="invalid-feedback" style={{ color: "#ff8888" }}>
                {errors.confirmPassword?.message}
              </div>
            </div>
            <div className="mb-4">
              <label htmlFor="country" className="form-label">
                Country
              </label>
              {
                isPending && <select>Loading...</select>
              }
              {
                isError && <select>Error loading countries</select>
              }
              {
                data && 
                <select {...register("country", {valueAsNumber: true})} className={`form-control ${errors.country ? "is-invalid" : ""}`}
                  style={{
                    backgroundColor: "#333633",
                    color: "#c2c2c2",
                    borderColor: "#4a4d4a",
                  }}>
                  <option value={0}>Select a country</option>
                  {data.map((country: Country) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
              }
              <div className="invalid-feedback" style={{ color: "#ff8888" }}>
                {errors.country?.message}
              </div>
            </div>
            <div className="d-grid gap-2 mb-3">
              <button
                type="submit"
                className="btn btn-primary"
                style={{
                  backgroundColor: "#5cb85c",
                  borderColor: "#5cb85c",
                  color: "#1d1f1c",
                }}
              >
                Register
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;
